import React, { FC, useCallback } from 'react';
import Link from 'next/link';
import Price from 'components/commercetools-ui/price';
import { ctColorToCssColor } from 'helpers/ctColorToCssColor';
import { useFormat } from 'helpers/hooks/useFormat';
import { ProductItem } from '.';
import { QuickView } from '../quick-view';
import { CustomImage } from 'lib/customImage';

type Size = [number, string];

export const ProductListItem: FC<ProductItem> = ({
  id,
  variants,
  locale,
  labelPrefix,
  paddingX = '4',
  paddingY = '4',
  disabled,
  isMobile,
  isTablet,
}) => {
  const currentVariant = variants?.find((variant) => variant?.attributes?.inStock === 'true') ?? variants?.[0];
  const { formatMessage } = useFormat({ name: 'product' });

  const sizeStringOrder = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'oneSize'];
  const sizes = [
    ...new Set(
      variants
        .map((variant) => {
          if (variant?.attributes?.size?.length === 0) return undefined;
          const sizeNumber = +variant?.attributes?.size;
          return !isNaN(sizeNumber)
            ? ([sizeNumber, 'number'] as Size)
            : ([sizeStringOrder.indexOf(variant?.attributes?.size), variant?.attributes?.size] as Size);
        })
        .filter((variant) => variant !== undefined),
    ),
  ];
  // get max size and min size
  const minSizeNumber = Math.min(...sizes?.map((size) => size?.[0]));
  const minSize: Size = [minSizeNumber, sizes?.find((size) => size?.[0] === minSizeNumber)?.[1]];
  const maxSizeNumber = Math.max(...sizes?.map((size) => size?.[0]));
  const maxSize: Size = [maxSizeNumber, sizes?.find((size) => size?.[0] === maxSizeNumber)?.[1]];

  const colors = [...new Set(variants?.map((variant) => variant?.attributes?.color))];
  const inStock = variants?.filter((variant) => variant?.attributes?.inStock === 'true')?.length > 0;

  const sizeToString = useCallback(
    (size: Size) => {
      if (size?.[1] === 'number') return size?.[0].toString();
      if (!size?.[1]) return undefined;
      return formatMessage({ id: labelPrefix + 'attributes.size.' + size?.[1], defaultMessage: size?.[1] });
    },
    [formatMessage, labelPrefix],
  );

  const topClassName = 'border-t pt-' + paddingY + ' ';
  const bottomClassName = 'pb-' + paddingY + ' ';

  const imageComponent = currentVariant?.images && currentVariant?.images.length > 0 && (
    // <Image
    //   src={currentVariant?.images[0]}
    //   alt={currentVariant?.title}
    //   className="h-full w-20 rounded-[4px] border-[1px] border-neutral-200 object-contain"
    // />
    <div className="w-[80px] rounded-[4px] border-[1px] border-neutral-200">
      <CustomImage imgSrc={currentVariant?.images[0]} width={80} height={106} placeholder />
    </div>
  );

  const priceComponent = (
    <div className="flex flex-col-reverse justify-center">
      <Price
        price={{ centAmount: currentVariant?.priceCentAmount }}
        className="text-sm font-semibold text-neutral-800"
      />
      <Price
        price={{ centAmount: currentVariant?.discountedCentAmount }}
        className={
          'text-sm font-normal text-zinc-500 line-through ' + (currentVariant?.discountedCentAmount ? '' : 'hidden')
        }
      />
    </div>
  );

  const colorsComponent = (
    <div className="flex w-fit flex-wrap items-center gap-1 text-xs font-bold text-neutral-800 lg:text-base lg:font-medium">
      {colors
        ?.filter((item) => item.length > 0)
        .map((item) => (
          <div
            key={item}
            className={(currentVariant.attributes.color === item ? '' : '') + ' h-4 w-4 rounded-sm'}
            style={{ background: ctColorToCssColor(item), border: '1px solid rgb(0, 0, 0, 0.1)' }}
          ></div>
        ))}
    </div>
  );

  const sizesComponent = (
    <div className="flex w-fit items-center text-xs font-bold text-neutral-800 lg:text-base lg:font-medium">
      {sizes?.length > 0 &&
        minSize?.[1] &&
        maxSize?.[1] &&
        (minSize?.[0] === maxSize?.[0] ? sizeToString(minSize) : `${sizeToString(minSize)} - ${sizeToString(maxSize)}`)}
    </div>
  );

  const inStockComponent = (
    <div className={'flex items-center text-sm font-medium ' + (inStock ? 'text-emerald-400' : 'text-red-400')}>
      {inStock
        ? formatMessage({ id: 'in_stock', defaultMessage: 'In Stock' })
        : formatMessage({ id: 'out_of_stock', defaultMessage: 'Out of stock' })}
    </div>
  );

  const optionsComponent = (
    <>
      <QuickView sizes={sizes} colors={colors} inStock={inStock} variant={currentVariant} variants={variants} id={id} />
      <Link href={variants?.[0]?.href ?? ''} locale={locale}>
        <a
          className="inline-flex flex-col items-center justify-center rounded bg-cyan-900 px-6 py-2.5 text-center text-sm font-bold text-white"
          onClick={(e) => disabled && e.preventDefault()}
          data-testid="choose-options"
        >
          {formatMessage({ id: 'chooseOptions', defaultMessage: 'Choose Options' })}
        </a>
      </Link>
    </>
  );

  return (
    <>
      <tr data-testid={`product-item-${id}`}>
        {!isTablet && (
          <td rowSpan={2} className={topClassName + bottomClassName + 'w-16'}>
            {imageComponent}
          </td>
        )}
        <td colSpan={isTablet ? 2 : 1} className={topClassName + (isTablet ? bottomClassName : '') + 'pr-' + paddingX}>
          <div className="flex items-center gap-4">
            {isTablet && imageComponent}
            <div className="flex w-fit flex-col gap-2">
              <div className="flex items-center justify-start gap-2">
                {currentVariant.tags.includes('new') && (
                  <div className="flex items-center justify-center gap-1 rounded bg-secondary px-2 py-1 text-white">
                    <p className="text-xs font-normal">{formatMessage({ id: 'new', defaultMessage: 'New' })}</p>
                    <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.1937 4.98125L7.79997 4.45625L6.26247 1.23125C6.14997 1.00625 5.84997 1.00625 5.73747 1.23125L4.19997 4.475L0.82497 4.98125C0.58122 5.01875 0.48747 5.3375 0.67497 5.50625L3.13122 8.0375L2.54997 11.5812C2.51247 11.825 2.75622 12.0312 2.98122 11.8813L6.03747 10.2125L9.07497 11.8813C9.28122 11.9938 9.54372 11.8062 9.48747 11.5812L8.90622 8.0375L11.3625 5.50625C11.5125 5.3375 11.4375 5.01875 11.1937 4.98125Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                )}

                {currentVariant.tags.includes('sale') && (
                  <div className="flex items-center justify-center gap-1 rounded bg-[#E77474] px-2 py-1 text-white">
                    <p className="text-xs font-normal">{formatMessage({ id: 'sale', defaultMessage: 'Sale' })}</p>
                    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_211_184)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5.2601 1.20969C5.42417 1.0923 5.641 1.07931 5.81792 1.17627C6.36504 1.47613 7.11161 2.01577 7.66876 2.89761C8.23232 3.78957 8.58064 4.9988 8.37038 6.58826C8.36791 6.60694 8.36706 6.62081 8.367 6.63083C8.39186 6.63517 8.4391 6.63758 8.51054 6.6164C8.71512 6.55576 9.01409 6.31551 8.9819 5.74971C8.96899 5.52283 9.10352 5.3134 9.31522 5.2308C9.52692 5.1482 9.76773 5.21117 9.91189 5.38684C10.6705 6.31124 11.5449 7.85631 11.6287 9.4849C11.6712 10.3114 11.5097 11.168 11.0168 11.9625C10.5246 12.7559 9.72553 13.4494 8.55615 13.9915C8.34078 14.0913 8.08522 14.0342 7.93283 13.8522C7.78043 13.6702 7.76912 13.4086 7.90524 13.2141C8.1604 12.8496 8.49801 11.882 7.78262 10.9877C7.77356 10.9764 7.76498 10.9647 7.75689 10.9527C7.72895 10.9111 7.70497 10.8827 7.66755 10.8383C7.64267 10.8088 7.61184 10.7722 7.56996 10.7201C7.4816 10.61 7.37428 10.4625 7.29508 10.2655C7.24412 10.1387 7.20934 10.0035 7.19191 9.85713C6.91137 10.0857 6.63847 10.368 6.42307 10.6986C5.9921 11.3601 5.78034 12.2249 6.22345 13.3179C6.30382 13.5162 6.25587 13.7433 6.10224 13.8922C5.94861 14.041 5.72003 14.0818 5.52441 13.9952C4.57229 13.5738 3.33288 12.6562 2.75581 11.2397C2.16059 9.77869 2.32039 7.91545 3.94633 5.74752L3.94633 5.74752C4.52866 4.9711 5.35445 3.48153 5.0484 1.72687C5.01373 1.52812 5.09602 1.32709 5.2601 1.20969ZM8.36938 6.65336C8.36922 6.65356 8.3686 6.65219 8.36804 6.6487C8.36927 6.65142 8.36955 6.65317 8.36938 6.65336ZM6.1605 2.71032C6.09608 4.35064 5.31814 5.66846 4.78633 6.37753C3.32844 8.32138 3.30264 9.79896 3.72821 10.8436C3.99146 11.4897 4.44619 12.0175 4.93744 12.4129C4.87354 11.5264 5.13335 10.7547 5.54332 10.1254C6.126 9.23111 7.00047 8.63589 7.63632 8.3527C7.82495 8.26869 8.04531 8.30305 8.1994 8.4405C8.35349 8.57796 8.41269 8.79297 8.35069 8.98993C8.17683 9.54219 8.22822 9.77161 8.26929 9.87379C8.29316 9.93314 8.32874 9.98799 8.38866 10.0626C8.39893 10.0754 8.41339 10.0927 8.4305 10.1131C8.48172 10.1743 8.55678 10.264 8.61494 10.3474C9.15478 11.0302 9.2986 11.7402 9.25662 12.3549C9.64189 12.0533 9.92326 11.7334 10.1246 11.409C10.4869 10.8249 10.6135 10.1881 10.5801 9.53883C10.5329 8.62096 10.1655 7.69679 9.71826 6.94073C9.48965 7.28539 9.16023 7.51898 8.80895 7.62311C8.48506 7.71911 8.09695 7.71397 7.77832 7.50889C7.42502 7.28151 7.27217 6.88361 7.32945 6.45057C7.50844 5.09747 7.20954 4.13658 6.78109 3.45844C6.5948 3.16359 6.38 2.91605 6.1605 2.71032Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_211_184">
                          <rect width="14" height="14" fill="white" transform="translate(0 0.5)" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                )}
              </div>{' '}
              <span className="text-sm font-semibold text-neutral-800">{currentVariant?.title}</span>
              <span className="text-sm font-normal text-zinc-500">{currentVariant?.description}</span>
            </div>
          </div>
        </td>
        <td className={topClassName + (isTablet ? bottomClassName : '') + 'px-' + paddingX}>{priceComponent}</td>
        {isTablet && <td className={topClassName + bottomClassName + 'px-' + paddingX}>{colorsComponent}</td>}
        {isTablet && <td className={topClassName + bottomClassName + 'px-' + paddingX}>{sizesComponent}</td>}
        {isTablet && <td className={topClassName + bottomClassName + 'px-' + paddingX}>{inStockComponent}</td>}
        {isTablet && <td className={topClassName + bottomClassName + 'pl-' + paddingX}>{optionsComponent}</td>}
      </tr>
      {!isTablet && (
        <tr>
          <td colSpan={2} className={bottomClassName + 'pr-' + paddingX}>
            <div className="flex w-full flex-row items-center justify-between sm:justify-start">
              <div className="flex flex-row items-center justify-start">
                <span className="mr-1 text-xs font-bold">
                  {formatMessage({ id: 'sizes', defaultMessage: 'Sizes' }) + ':'}
                </span>
                {sizesComponent}
              </div>
              <div className="w-4"></div>
              <div className="flex h-fit flex-row items-center justify-start">
                <span className="mr-1 text-xs font-bold">
                  {formatMessage({ id: 'colors', defaultMessage: 'Colors' }) + ':'}
                </span>
                {colorsComponent}
              </div>
              <div className="hidden grow flex-row justify-end sm:flex md:hidden">{optionsComponent}</div>
            </div>
          </td>
        </tr>
      )}
      <tr className="table-row sm:hidden">
        <td colSpan={4} className={bottomClassName + 'pr-' + paddingX}>
          <div className="flex flex-row items-center justify-end">{optionsComponent}</div>
        </td>
      </tr>
    </>
  );
};
